<template>
  <div class="pay-plans-list-page">
    <EXPermissionDeniedState v-if="state.forbidden" />
    <PayPlansTable
      v-else
      :rows="state.rows"
      :filter-chips="state.filters.chips"
      :page="state.page"
      :page-count="pageCount"
      :loading="state.loading"
      :error="state.error"
      :as-of-enabled="state.asOf.enabled"
      :as-of-date="asOfDate"
      :as-of-is-today="asOfIsToday"
      :no-pay-ranges="showNoPayRanges"
      :blocked-reason-enabled="proposedPayPlanDrawerEnabled"
      empty-title="No employees found"
      empty-message="Your search or filters did not match any pay plans."
      :can-add-pay-plan="false"
      @filters-changed="onSearchFiltersChanged"
      @retry="fetchRows"
      @update:page="onPageChange"
      @remove-filter="onRemoveFilter"
      @open-more-filters="onOpenMoreFilters"
      @row-click="onRowClick"
      @open-pay-plan="openVersionDrawer"
      @add-pay-plan="openAddPlan"
      @propose-first-plan="openProposePlan"
      @propose-pay-plan="openProposePlan"
      @propose-changes="openProposeChanges"
      @evaluate-eligibility="openEvaluateEligibility"
      @open-proposal="openProposalDrawer"
      @open-blocking-item="openBlockingItem"
      @void-change="openVoidProposal"
      @open-as-of-picker="openAsOfPicker"
      @reset-as-of="resetAsOfToToday"
      @go-to-pay-ranges="emit('go-to-pay-ranges')"
      @as-of-version-change="(row, version) => applyAsOfFromVersion(version)"
    />

    <DatePicker
      v-if="state.asOf.enabled && state.asOf.pickerOpen"
      :isDatePickerOpen="state.asOf.pickerOpen"
      :value="asOfDate"
      :modal="true"
      @onApplyDate="applyAsOfDate"
      @onClose="closeAsOfPicker"
    />

    <ProposedPayPlanDrawerConnected
      v-if="proposedPayPlanDrawerEnabled"
      ref="proposalDrawer"
      :proxy="proxy"
      :fetch-rows="fetchRows"
    />

    <!-- Current drawer — the row's In Force plan covering today (ER-12118) -->
    <CurrentPayPlanDrawerConnected
      v-if="proposedPayPlanDrawerEnabled"
      ref="currentDrawer"
      :proxy="proxy"
      :fetch-rows="fetchRows"
      @propose-changes="onDrawerProposeChanges"
    />

    <!-- Version pane — Future / Voided versions, read-only (ER-12119) -->
    <PayPlanVersionPaneConnected
      v-if="proposedPayPlanDrawerEnabled"
      ref="versionPane"
      :proxy="proxy"
      :fetch-rows="fetchRows"
      @propose-changes="onDrawerProposeChanges"
    />

    <!-- Lapsed drawer — a committed version whose period ended (ER-12113) -->
    <LapsedPayPlanDrawerConnected
      v-if="proposedPayPlanDrawerEnabled"
      ref="lapsedDrawer"
      :proxy="proxy"
      :fetch-rows="fetchRows"
    />

    <PayPlansTableFilterModal
      :value="state.filters.showFilterDialog"
      :departments="state.departments"
      :loading="state.departmentsLoading"
      :initial-fair-pay-status="filterModalSelection.fairPayStatus"
      :initial-pay-plan="filterModalSelection.payPlan"
      :initial-change="filterModalSelection.change"
      :initial-department="filterModalSelection.department"
      :initial-scope="filterModalSelection.scope"
      @input="state.filters.showFilterDialog = $event"
      @close="closeFilterModal"
      @apply="onFiltersApplied"
      @reset="resetFiltersToDefaults"
      @search="searchDepartments"
      @clear="searchDepartments('')"
    />

    <div
      v-if="wizard.type"
      class="pay-plans-list-page__overlay"
      @click.self="close"
    >
      <FirstPayPlanWizard
        v-if="wizard.type === 'contract'"
        :step="wizard.step"
        :effective-date="wizard.effectiveDate"
        :effective-by="wizard.effectiveBy"
        :country="wizard.country"
        :currency="wizard.currency"
        :base-salary="wizard.baseSalary"
        :pay-basis="wizard.payBasis"
        :pay-basis-label="wizard.payBasisLabel"
        :calculator-mode="wizard.calculatorMode"
        :pay-range="wizard.payRange"
        :readiness-module-enabled="wizard.readinessModuleEnabled"
        :disabled-ranges="wizard.disabledRanges"
        :next-conflict-date="wizard.nextConflictDate"
        :effective-to-enabled="wizard.effectiveToEnabled"
        :submitting="wizard.submitting"
        @update:effectiveDate="wizard.effectiveDate = $event"
        @update:effectiveBy="wizard.effectiveBy = $event"
        @update:baseSalary="wizard.baseSalary = $event"
        @update:country="wizard.country = $event"
        @update:currency="wizard.currency = $event"
        @update:payBasis="wizard.payBasis = $event"
        @update:payBasisLabel="wizard.payBasisLabel = $event"
        @next="goNext"
        @back="goBack"
        @create-pay-plan="submitContract"
        @close="close"
      />
      <AddFirstFairPayPlanProposalWizard
        v-else
        :step="wizard.step"
        :effective-date="wizard.effectiveDate"
        :country="wizard.country"
        :currency="wizard.currency"
        :base-salary="wizard.baseSalary"
        :pay-basis="wizard.payBasis"
        :pay-basis-label="wizard.payBasisLabel"
        :calculator-mode="wizard.calculatorMode"
        :pay-range="wizard.payRange"
        :approvers="wizard.approvers"
        :approver-items="wizard.approverItems"
        :approver-loading="wizard.approverLoading"
        :approver-has-more="wizard.approverHasMore"
        :submitting="wizard.submitting"
        @update:effectiveDate="wizard.effectiveDate = $event"
        @update:baseSalary="wizard.baseSalary = $event"
        @update:country="wizard.country = $event"
        @update:currency="wizard.currency = $event"
        @update:payBasis="wizard.payBasis = $event"
        @update:payBasisLabel="wizard.payBasisLabel = $event"
        @add-approver="addApprover"
        @remove-approver="removeApprover"
        @search-approvers="searchApprovers"
        @load-more-approvers="loadMoreApprovers"
        @next="goNext"
        @back="goBack"
        @save-as-draft="handleSaveProposalDraft"
        @close="close"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import { LD_FLAG_IDEA_MDP_429 } from "@components/constants/launchDarklyFlagKeys";
import launchDarklyService from "@/services/launchdarkly";
import DatePicker from "@components/DatePicker/DatePicker.vue";
import PayPlansTable from "@components/PayPlansTable/PayPlansTable.vue";
import EXPermissionDeniedState from "@components/EXPermissionDeniedState/EXPermissionDeniedState.vue";
import PayPlansTableFilterModal from "@components/PayPlansTableFilterModal/PayPlansTableFilterModal.vue";
import FirstPayPlanWizard from "@components/FirstPayPlanWizard/FirstPayPlanWizard.vue";
import AddFirstFairPayPlanProposalWizard from "@components/AddFirstFairPayPlanProposalWizard/AddFirstFairPayPlanProposalWizard.vue";
import ProposedPayPlanDrawerConnected from "@components/ProposedPayPlanDrawer/ProposedPayPlanDrawerConnected.vue";
import CurrentPayPlanDrawerConnected from "@components/CurrentPayPlanDrawer/CurrentPayPlanDrawerConnected.vue";
import PayPlanVersionPaneConnected from "@components/PayPlanVersionPane/PayPlanVersionPaneConnected.vue";
import LapsedPayPlanDrawerConnected from "@components/LapsedPayPlanDrawer/LapsedPayPlanDrawerConnected.vue";
import { usePayPlansListPageState } from "./composables/usePayPlansListPageState";
import { usePayPlanWizards } from "./composables/usePayPlanWizards";

const emit = defineEmits(["envelope-counts", "go-to-pay-ranges"]);
const { proxy } = getCurrentInstance() || {};

const {
  state,
  pageCount,
  noInForcePayRange,
  filterModalSelection,
  asOfDate,
  asOfIsToday,
  fetchRows,
  loadInForcePayRangeCount,
  loadAsOfFlag,
  openAsOfPicker,
  applyAsOfDate,
  closeAsOfPicker,
  resetAsOfToToday,
  applyAsOfFromVersion,
  loadDepartments,
  searchDepartments,
  loadFiltersOnReload,
  onSearchFiltersChanged,
  onPageChange,
  onOpenMoreFilters,
  closeFilterModal,
  onFiltersApplied,
  onRemoveFilter,
  resetFiltersToDefaults,
  onRowClick,
} = usePayPlansListPageState();

const {
  wizard,
  openAddPlan,
  openProposePlan,
  openProposeChanges,
  openEvaluateEligibility,
  close,
  goNext,
  goBack,
  addApprover,
  removeApprover,
  searchApprovers,
  loadMoreApprovers,
  submitContract,
  submitProposal,
} = usePayPlanWizards({ fetchRows, proxy });

const proposalDrawer = ref(null);
const currentDrawer = ref(null);
const versionPane = ref(null);
const lapsedDrawer = ref(null);
/** The row whose drawer is open — Propose Changes re-enters the wizard for it. */
let lastDrawerRow = null;

/** Propose Changes from the Current drawer / Version pane (ER-12109 flow). */
function onDrawerProposeChanges() {
  if (lastDrawerRow) openProposeChanges(lastDrawerRow);
}
/** Fair Pay MVP (idea-MDP-429) — proposal drawer consumer is flag-gated (R21). */
const proposedPayPlanDrawerEnabled = ref(false);
/** First-run empty state (no In Force pay range) ships behind the same idea-MDP-429 flag. */
const showNoPayRanges = computed(
  () => proposedPayPlanDrawerEnabled.value && noInForcePayRange.value
);

async function loadProposedPayPlanDrawerFlag() {
  try {
    proposedPayPlanDrawerEnabled.value =
      (await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_429, false)) === true;
  } catch {
    proposedPayPlanDrawerEnabled.value = false;
  }
}

function proposalDrawerArgs(row) {
  const employeeId = row?.employee?.id || row?.id || null;
  const proposalId = row?.changes?.recordId ?? null;
  if (!proposalId && !employeeId) return null;
  // Hand the drawer the row's approval lifecycle so it hides draft-only
  // actions (Add Approver / Submit) for proposals that moved on.
  return {
    employeeId,
    proposalId,
    approvalStatus: row?.proposalApprovalStatus || null,
  };
}

function openProposalDrawer(row) {
  if (!proposedPayPlanDrawerEnabled.value) return;
  const args = proposalDrawerArgs(row);
  if (args) proposalDrawer.value?.open(args);
}

/**
 * "Not allowed" (ER-12691): open the item that blocks Add / Propose. A pending or
 * first-plan proposal opens the proposal drawer; a committed future plan opens
 * that version's drawer.
 */
function openBlockingItem(row, blocked) {
  if (!proposedPayPlanDrawerEnabled.value || !blocked) return;
  if (blocked.reason === "future_plan_committed") {
    const version = (row?.payPlanVersions || []).find(
      (item) => String(item.versionId) === String(blocked.versionId)
    );
    openVersionDrawer(row, version || { versionId: blocked.versionId, state: "future" });
    return;
  }
  const employeeId = row?.employee?.id || row?.id || null;
  const proposalId = blocked.versionId ?? blocked.proposalId ?? null;
  if (!proposalId) return;
  proposalDrawer.value?.open({
    employeeId,
    proposalId,
    approvalStatus: row?.proposalApprovalStatus || null,
  });
}

/** ⋮ > "Void proposal…" on the chip: open the proposal with its reason-gated void dialog up. */
function openVoidProposal(row) {
  if (!proposedPayPlanDrawerEnabled.value) return;
  const args = proposalDrawerArgs(row);
  if (args) proposalDrawer.value?.openWithVoidDialog(args);
}

/**
 * The green "Current Pay Plan" slider opens the drawer for the version being
 * shown (ER-12116 routing table): Voided / Future → the read-only pane,
 * lapsed → the Lapsed drawer, otherwise the Current drawer.
 */
function openVersionDrawer(row, version = null) {
  if (!proposedPayPlanDrawerEnabled.value) return;
  const employeeId = row?.employee?.id || row?.id || null;
  const selected = version || row?.currentPayPlan || null;
  const versionId = selected?.versionId ?? row?.payPlan?.version_id ?? null;
  if (!employeeId || !versionId) return;

  // The drawers' Propose Changes re-enters the wizard for the row that opened
  // them, so remember it.
  lastDrawerRow = row;

  const employee = {
    name: `${row.employee?.firstName || ""} ${row.employee?.lastName || ""}`.trim(),
    title: row.employee?.positionTitle || "",
    avatarUrl: row.employee?.avatarUrl || null,
  };

  const status = String(selected?.status || "").toUpperCase();
  const state = String(selected?.state || "").toLowerCase();

  if (status === "VOIDED" || state === "future") {
    versionPane.value?.open({ employeeId, versionId, employee });
    return;
  }
  if (state === "lapsed") {
    lapsedDrawer.value?.open({ employeeId, versionId });
    return;
  }
  currentDrawer.value?.open({ employeeId, versionId, employee });
}

async function handleSaveProposalDraft() {
  const { type, employeeId } = wizard;
  const proposal = await submitProposal();
  if (proposedPayPlanDrawerEnabled.value && type === "changes" && proposal) {
    proposalDrawer.value?.open({ employeeId, proposal, approvalStatus: "Draft" });
  }
}

function emitEnvelopeCounts() {
  if (state.currentPayPlanCount == null && state.activePayRangeCount == null) return;
  emit("envelope-counts", {
    currentPayPlanCount: state.currentPayPlanCount,
    activePayRangeCount: state.activePayRangeCount,
  });
}

watch(() => state.currentPayPlanCount, emitEnvelopeCounts);
watch(() => state.activePayRangeCount, emitEnvelopeCounts);

onMounted(async () => {
  loadFiltersOnReload();
  await Promise.all([loadDepartments(), loadProposedPayPlanDrawerFlag(), loadAsOfFlag()]);
  await Promise.all([
    fetchRows(),
    proposedPayPlanDrawerEnabled.value ? loadInForcePayRangeCount() : null,
  ]);
  emitEnvelopeCounts();
});
</script>

<style scoped>
.pay-plans-list-page {
  width: 100%;
  padding: 8px 0 24px;
}

.pay-plans-list-page__overlay {
  position: fixed;
  inset: 0;
  z-index: 4000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 48px 16px 24px;
  overflow-y: auto;
  background: rgba(16, 24, 40, 0.45);
}
</style>
